/**
 * Small wrapper around the theme REST API.
 *
 * @package AGDS
 * @category Modules
 * @module ApiService
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
class ApiService {
    /**
     * Creates an API service instance.
     *
     * @param {string} baseUrl - API base URL.
     * @param {string} nonce - WordPress REST nonce.
     */
    constructor(baseUrl, nonce) {
        this.baseUrl = baseUrl;
        this.nonce = nonce;
    }

    /**
     * Sends a GET request.
     *
     * @param {string} endpoint - Request endpoint.
     * @param {object} params - Query parameters.
     * @returns {Promise<object>}
     */
    async get(endpoint, params = {}) {
        const url = new URL(this.baseUrl + endpoint);

        Object.entries(params || {}).forEach(([key, value]) => {
            if (value === undefined || value === null) return;

            if (Array.isArray(value)) {
                value.forEach((v) => url.searchParams.append(key, String(v)));
            } else {
                url.searchParams.append(key, String(value));
            }
        });

        const options = {
            method: "GET",
            headers: {
                "X-WP-Nonce": this.nonce,
                "X-Requested-With": "XMLHttpRequest",
            },
        };

        return this.request(url, options);
    }

    /**
     * Sends a POST request.
     *
     * @param {string} endpoint - Request endpoint.
     * @param {object} body - Request body.
     * @returns {Promise<object>}
     */
    async post(endpoint, body = {}) {
        const url = new URL(this.baseUrl + endpoint);

        const options = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": this.nonce,
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
        };

        return this.request(url, options);
    }

    /**
     * Runs fetch and normalizes API errors.
     *
     * @param {URL} url - Request URL.
     * @param {object} options - Fetch options.
     * @returns {Promise<object>}
     */
    async request(url, options = {}) {
        try {
            const response = await fetch(url.toString(), options);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const message = errorData.message || `HTTP error! Status: ${response.status}`;
                throw new Error(message);
            }

            return await response.json();
        } catch (error) {
            console.error("API Service Error:", error);
            return { html: "", has_more: false };
        }
    }

    /**
     * Loads posts for the load-more flow.
     *
     * @param {string} postType - Post type.
     * @param {number} page - Page to load.
     * @param {object} extraParams - Extra query parameters.
     * @returns {Promise<object>}
     */
    postsLoadMore(postType, page, extraParams = {}) {
        const params = Object.assign({ post_type: postType, page: page }, extraParams);
        return this.get("posts-loader", params);
    }

    /**
     * Loads filtered posts.
     *
     * @param {string} postType - Post type.
     * @param {number} page - Page to load.
     * @param {object} filters - Filter data.
     * @returns {Promise<object>}
     */
    postsFilter(postType, page, filters = {}) {
        return this.post("posts-loader", {
            post_type: postType,
            page: page,
            filters: filters,
        });
    }
}

export const api = new ApiService(agds.rest.url, agds.rest.nonce);
